/* CRM Inbox — HubSpot/Notion-style leads inbox for habita.co
 *
 * Two views: Table (default) + Kanban. The choice persists to localStorage
 * so the user's last view is restored on next visit. Quick filters live in
 * the toolbar; search is client-side over the current dataset (we already
 * receive up to 200 leads on the initial load). Kanban is a read-only
 * arrangement in the MVP — clicking a card opens the detail page; drag-to-
 * change-status is deferred to FASE 4B.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, Filter, LayoutGrid, Table as TableIcon, Plus, ChevronDown,
  Loader2, X, RefreshCw, Zap,
} from 'lucide-react';
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
  useDroppable, useDraggable, DragOverlay,
} from '@dnd-kit/core';
import { api, friendlyError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const STATUS_META = {
  nuevo:            { label: 'Nuevo',            color: 'bg-blue-50 text-blue-700 border-blue-200',       dot: 'bg-blue-500' },
  contactado:       { label: 'Contactado',       color: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  interesado:       { label: 'Interesado',       color: 'bg-cyan-50 text-cyan-700 border-cyan-200',       dot: 'bg-cyan-500' },
  visita:           { label: 'Visita',           color: 'bg-amber-50 text-amber-700 border-amber-200',    dot: 'bg-amber-500' },
  negociacion:      { label: 'Negociación',      color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  reserva:          { label: 'Reserva',          color: 'bg-teal-50 text-teal-700 border-teal-200',       dot: 'bg-teal-500' },
  cerrado_ganado:   { label: 'Cerrado ganado',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  cerrado_perdido:  { label: 'Cerrado perdido',  color: 'bg-rose-50 text-rose-700 border-rose-200',       dot: 'bg-rose-500' },
  // Backwards-compat for legacy 'cerrado' / 'perdido'
  cerrado:          { label: 'Cerrado',          color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  perdido:          { label: 'Perdido',          color: 'bg-rose-50 text-rose-700 border-rose-200',       dot: 'bg-rose-500' },
};
const STATUS_ORDER = ['nuevo', 'contactado', 'interesado', 'visita', 'negociacion', 'reserva', 'cerrado_ganado', 'cerrado_perdido'];
const PRIORITY_META = {
  alta:  { label: 'Alta',  color: 'text-rose-600',   dot: '🔴' },
  media: { label: 'Media', color: 'text-amber-600',  dot: '🟡' },
  baja:  { label: 'Baja',  color: 'text-emerald-600', dot: '🟢' },
};

const LAST_VIEW_KEY = 'habita:crm:last_view';

function relTime(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return 'hace segundos';
    if (diff < 3_600_000) return `hace ${Math.floor(diff / 60_000)} min`;
    if (diff < 86_400_000) return `hace ${Math.floor(diff / 3_600_000)} h`;
    if (diff < 30 * 86_400_000) return `hace ${Math.floor(diff / 86_400_000)} d`;
    return new Date(iso).toLocaleDateString('es-CO');
  } catch { return '—'; }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.nuevo;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function PriorityPill({ priority }) {
  const meta = PRIORITY_META[priority] || PRIORITY_META.media;
  return <span className={`text-xs font-semibold ${meta.color} tabular-nums`}>{meta.dot} {meta.label}</span>;
}

function TypeBadge({ leadType }) {
  const isSeller = leadType === 'vendedor';
  const isBuyer = leadType === 'comprador';
  if (!isSeller && !isBuyer) return <span className="text-gray-300 text-xs">—</span>;
  const cls = isSeller
    ? 'bg-amber-100 text-amber-700'
    : 'bg-sky-100 text-sky-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`} data-testid={`lead-type-${leadType}`}>
      {isSeller ? 'Vendedor' : 'Comprador'}
    </span>
  );
}

function DashCards({ dash }) {
  if (!dash) return null;
  const cards = [
    { label: 'Nuevos', value: dash.statuses?.nuevo, testid: 'crm-kpi-new' },
    { label: 'Contactados', value: dash.statuses?.contactado, testid: 'crm-kpi-contactado' },
    { label: 'En visita', value: dash.statuses?.visita, testid: 'crm-kpi-visita' },
    { label: 'Negociación', value: dash.statuses?.negociacion, testid: 'crm-kpi-negot' },
    { label: 'Cerrados', value: dash.statuses?.cerrado, testid: 'crm-kpi-closed' },
    { label: 'Tiempo resp. prom.', value: dash.avg_response_minutes != null ? `${dash.avg_response_minutes} min` : '—', testid: 'crm-kpi-response' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {cards.map(c => (
        <div key={c.label} className="bg-white border border-gray-100 rounded-2xl p-3" data-testid={c.testid}>
          <p className="text-[11px] uppercase tracking-widest text-gray-500 font-medium">{c.label}</p>
          <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
            {c.value != null ? c.value : 0}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────── Table view

function TableView({ leads, onSelect }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="crm-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-3 py-3">Tipo</th>
              <th className="text-left px-3 py-3">Contacto</th>
              <th className="text-left px-3 py-3">Propiedad</th>
              <th className="text-left px-3 py-3">Estado</th>
              <th className="text-left px-3 py-3">Prioridad</th>
              <th className="text-left px-3 py-3">Fuente</th>
              <th className="text-left px-3 py-3">Asesor</th>
              <th className="text-right px-4 py-3">Actualizado</th>
            </tr>
          </thead>
          <tbody>
            {leads.map(lead => (
              <tr
                key={lead.id}
                onClick={() => onSelect(lead.id)}
                className="border-b border-gray-50 hover:bg-habita-mint/30 cursor-pointer transition-colors"
                data-testid={`crm-row-${lead.id}`}
              >
                <td className="px-4 py-3 font-medium text-gray-900">{lead.name}</td>
                <td className="px-3 py-3"><TypeBadge leadType={lead.lead_type} /></td>
                <td className="px-3 py-3 text-gray-600 text-xs">
                  <div className="flex flex-col">
                    {lead.email && <span className="truncate max-w-[180px]">{lead.email}</span>}
                    {lead.phone && <span className="text-gray-500">{lead.phone}</span>}
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-700 truncate max-w-[220px]">
                  {lead.property?.title || <span className="text-gray-400">—</span>}
                </td>
                <td className="px-3 py-3"><StatusBadge status={lead.status} /></td>
                <td className="px-3 py-3"><PriorityPill priority={lead.priority} /></td>
                <td className="px-3 py-3 text-xs text-gray-600 capitalize">{lead.source}</td>
                <td className="px-3 py-3 text-gray-700 text-xs truncate max-w-[140px]">
                  {lead.assigned_agent?.name || <span className="text-gray-400">Sin asignar</span>}
                </td>
                <td className="px-4 py-3 text-right text-xs text-gray-500">{relTime(lead.updated_at)}</td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-gray-400">Sin leads con esos filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Kanban view — Drag & Drop

function KanbanCard({ lead, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `card-${lead.id}`,
    data: { lead },
  });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => { if (!isDragging) { e.stopPropagation(); onSelect(lead.id); } }}
      className="w-full text-left bg-white rounded-xl p-3 border border-gray-100 hover:border-habita hover:shadow-sm transition-all cursor-grab active:cursor-grabbing"
      data-testid={`kanban-card-${lead.id}`}
    >
      <p className="font-medium text-sm text-gray-900 truncate">{lead.name}</p>
      <div className="mt-1"><TypeBadge leadType={lead.lead_type} /></div>
      {lead.property?.title && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{lead.property.title}</p>}
      <div className="flex items-center justify-between mt-2 text-xs">
        <PriorityPill priority={lead.priority} />
        <span className="text-gray-400">{relTime(lead.updated_at)}</span>
      </div>
    </div>
  );
}

function KanbanColumn({ status, leads, onSelect }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${status}`, data: { status } });
  const meta = STATUS_META[status];
  return (
    <div
      ref={setNodeRef}
      className={`bg-gray-50/60 rounded-2xl p-3 transition-colors ${isOver ? 'ring-2 ring-habita bg-habita/5' : ''}`}
      data-testid={`kanban-col-${status}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
          <p className="text-sm font-semibold text-gray-800">{meta.label}</p>
        </div>
        <span className="text-xs text-gray-500 tabular-nums">{leads.length}</span>
      </div>
      <div className="space-y-2 min-h-[100px]">
        {leads.map(lead => <KanbanCard key={lead.id} lead={lead} onSelect={onSelect} />)}
      </div>
    </div>
  );
}

function KanbanView({ leads, onSelect, onStatusChange }) {
  const [activeLead, setActiveLead] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_ORDER.map(s => [s, []]));
    for (const lead of leads) {
      // Normalise legacy statuses on the fly
      const st = lead.status === 'cerrado' ? 'cerrado_ganado'
              : lead.status === 'perdido' ? 'cerrado_perdido'
              : (lead.status || 'nuevo');
      if (g[st]) g[st].push(lead); else g.nuevo.push(lead);
    }
    return g;
  }, [leads]);

  const onDragStart = (evt) => setActiveLead(evt.active.data.current?.lead || null);
  const onDragEnd = (evt) => {
    setActiveLead(null);
    const overId = evt.over?.id;
    if (!overId || !overId.startsWith('col-')) return;
    const newStatus = overId.replace('col-', '');
    const lead = evt.active.data.current?.lead;
    if (!lead) return;
    const normalizedCurrent = lead.status === 'cerrado' ? 'cerrado_ganado'
                             : lead.status === 'perdido' ? 'cerrado_perdido'
                             : lead.status;
    if (normalizedCurrent === newStatus) return;
    onStatusChange(lead, newStatus);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="crm-kanban">
        {STATUS_ORDER.map(status => (
          <KanbanColumn key={status} status={status} leads={grouped[status]} onSelect={onSelect} />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeLead && (
          <div className="bg-white rounded-xl p-3 border-2 border-habita shadow-lg opacity-90 w-full max-w-xs">
            <p className="font-medium text-sm text-gray-900 truncate">{activeLead.name}</p>
            {activeLead.property?.title && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{activeLead.property.title}</p>}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

// ─────────────────────────────────────── Manual create modal

function CreateLeadModal({ open, onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', source: 'habita', priority: 'media' });
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const doc = await api.crmCreateLead(form);
      toast({ title: 'Lead creado', description: `${doc.name} agregado al CRM.` });
      onCreated(doc);
      onClose();
    } catch (err) {
      toast({ title: 'No pudimos crear el lead', description: friendlyError(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="crm-create-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl font-bold text-habita-dark">Nuevo lead</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required autoFocus data-testid="crm-create-name" placeholder="Nombre completo" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 h-10 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm" />
          <input type="email" data-testid="crm-create-email" placeholder="Email (opcional)" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 h-10 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm" />
          <input data-testid="crm-create-phone" placeholder="Teléfono (opcional)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 h-10 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="px-3 h-10 rounded-lg border border-gray-200 text-sm bg-white">
              {['habita','google','facebook','instagram','whatsapp','email','campana','directo'].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </select>
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="px-3 h-10 rounded-lg border border-gray-200 text-sm bg-white">
              <option value="alta">🔴 Alta</option>
              <option value="media">🟡 Media</option>
              <option value="baja">🟢 Baja</option>
            </select>
          </div>
          <button type="submit" disabled={saving} data-testid="crm-create-submit" className="w-full h-11 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Crear lead
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Main page

export default function CRMInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [view, setView] = useState(() => {
    try { return localStorage.getItem(LAST_VIEW_KEY) || 'table'; } catch { return 'table'; }
  });
  useEffect(() => {
    try { localStorage.setItem(LAST_VIEW_KEY, view); } catch { /* ignore */ }
  }, [view]);

  const [leads, setLeads] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [source, setSource] = useState('');
  const [leadType, setLeadType] = useState('');
  const [city, setCity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const [l, d] = await Promise.all([
        api.crmListLeads({ status, priority, source, lead_type: leadType, city, date_from: dateFrom, q, limit: 200 }),
        api.crmDashboard(),
      ]);
      setLeads(l.items || []);
      setDash(d);
    } catch (err) {
      toast({ title: 'No pudimos cargar el CRM', description: friendlyError(err), variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [status, priority, source, leadType, city, dateFrom]);

  const filteredLeads = useMemo(() => {
    if (!q.trim()) return leads;
    const needle = q.trim().toLowerCase();
    return leads.filter(l =>
      l.name?.toLowerCase().includes(needle)
      || l.email?.toLowerCase().includes(needle)
      || l.phone?.toLowerCase().includes(needle)
      || l.property?.title?.toLowerCase().includes(needle),
    );
  }, [leads, q]);

  const importHistorical = async () => {
    setImporting(true);
    try {
      const r = await api.crmImportHistorical(90);
      toast({ title: 'Histórico importado', description: `${r.leads_created_or_updated} leads procesados de los últimos 90 días.` });
      await load();
    } catch (err) {
      toast({ title: 'No pudimos importar', description: friendlyError(err), variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const openLead = (id) => navigate(`/crm/lead/${id}`);

  const changeStatusOptimistic = async (lead, newStatus) => {
    // Optimistic update: reflect in UI first, revert on error
    const prev = lead.status;
    setLeads(current => current.map(l => l.id === lead.id ? { ...l, status: newStatus, updated_at: new Date().toISOString() } : l));
    try {
      await api.crmUpdateLead(lead.id, { status: newStatus });
      toast({ title: 'Estado actualizado', description: `${lead.name} → ${STATUS_META[newStatus]?.label || newStatus}` });
      // Refresh the dashboard counts in the background
      api.crmDashboard().then(setDash).catch(() => {});
    } catch (err) {
      setLeads(current => current.map(l => l.id === lead.id ? { ...l, status: prev } : l));
      toast({ title: 'No pudimos actualizar', description: friendlyError(err), variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="crm-inbox">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-habita">CRM</span>
                <span className="text-xs text-gray-400">FASE 4A · MVP</span>
              </div>
              <h1 className="font-serif text-3xl font-bold text-habita-dark">Bandeja de leads</h1>
              <p className="text-sm text-gray-500 mt-1">Todos tus clientes potenciales en un solo lugar.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/crm/integraciones"
                className="flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold text-habita hover:bg-habita/5 border border-habita/20"
                data-testid="crm-integrations-link"
              >
                <Zap className="w-4 h-4" /> Integraciones
              </Link>
              {user?.role === 'admin' && (
                <button
                  onClick={importHistorical}
                  disabled={importing}
                  className="flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-60"
                  data-testid="crm-import-btn"
                  title="Trae inspecciones + mensajes de los últimos 90 días como leads"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Importar histórico
                </button>
              )}
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold bg-habita text-white hover:bg-habita-dark"
                data-testid="crm-new-btn"
              >
                <Plus className="w-4 h-4" /> Nuevo lead
              </button>
            </div>
          </div>

          {dash && <div className="mt-6"><DashCards dash={dash} /></div>}
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-[1400px] mx-auto px-6 py-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar por nombre, email, teléfono o propiedad..."
              className="w-full pl-10 pr-3 h-10 rounded-full border border-gray-200 focus:border-habita outline-none text-sm bg-white"
              data-testid="crm-search"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <FilterSelect label="Estado" value={status} onChange={setStatus} options={[{ v: '', l: 'Todos' }, ...STATUS_ORDER.map(s => ({ v: s, l: STATUS_META[s].label }))]} testid="filter-status" />
            <FilterSelect label="Prioridad" value={priority} onChange={setPriority} options={[{ v: '', l: 'Todas' }, { v: 'alta', l: '🔴 Alta' }, { v: 'media', l: '🟡 Media' }, { v: 'baja', l: '🟢 Baja' }]} testid="filter-priority" />
            <FilterSelect label="Tipo" value={leadType} onChange={setLeadType} options={[{ v: '', l: 'Todos' }, { v: 'vendedor', l: '🏠 Vendedores' }, { v: 'comprador', l: '🔑 Compradores' }]} testid="filter-lead-type" />
            <FilterSelect label="Fuente" value={source} onChange={setSource} options={[{ v: '', l: 'Todas' }, ...['habita','google','facebook','instagram','whatsapp','email','campana','directo','valoracion','advisor'].map(s => ({ v: s, l: s }))]} testid="filter-source" />

            <div className="flex items-center bg-white rounded-full border border-gray-200 p-1">
              <button
                onClick={() => setView('table')}
                className={`flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold transition-colors ${view === 'table' ? 'bg-habita text-white' : 'text-gray-600 hover:text-habita'}`}
                data-testid="view-toggle-table"
              >
                <TableIcon className="w-3.5 h-3.5" /> Tabla
              </button>
              <button
                onClick={() => setView('kanban')}
                className={`flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold transition-colors ${view === 'kanban' ? 'bg-habita text-white' : 'text-gray-600 hover:text-habita'}`}
                data-testid="view-toggle-kanban"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Kanban
              </button>
            </div>

            <button
              onClick={() => { setRefreshing(true); load(); }}
              disabled={refreshing || loading}
              className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-habita text-gray-600"
              data-testid="crm-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-3">
          Mostrando {filteredLeads.length} de {leads.length} leads {q && `(filtro "${q}")`}
        </p>

        {/* Second filters row — advanced */}
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <input
            value={city}
            onChange={e => setCity(e.target.value)}
            onBlur={() => load()}
            onKeyDown={e => { if (e.key === 'Enter') load(); }}
            placeholder="Ciudad (ej: Bogotá)"
            className="h-9 px-3 rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-700 focus:border-habita outline-none w-40"
            data-testid="filter-city"
          />
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Desde:</span>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="h-9 px-3 rounded-full border border-gray-200 bg-white text-xs text-gray-700 focus:border-habita outline-none"
              data-testid="filter-date-from"
            />
          </div>
          {(city || dateFrom || status || priority || source || leadType) && (
            <button
              onClick={() => { setCity(''); setDateFrom(''); setStatus(''); setPriority(''); setSource(''); setLeadType(''); }}
              className="text-xs text-rose-600 hover:text-rose-800 font-semibold"
              data-testid="filter-clear-all"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-[1400px] mx-auto px-6 pb-12">
        {loading ? (
          <div className="text-center py-24 text-gray-400"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>
        ) : view === 'table' ? (
          <TableView leads={filteredLeads} onSelect={openLead} />
        ) : (
          <KanbanView leads={filteredLeads} onSelect={openLead} onStatusChange={changeStatusOptimistic} />
        )}
      </div>

      <CreateLeadModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => load()} />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="relative inline-block">
      <Filter className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="appearance-none pl-8 pr-8 h-9 rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-700 focus:border-habita outline-none capitalize"
        data-testid={testid}
      >
        {options.map((o, i) => (
          <option key={i} value={o.v}>{o.v ? o.l : `${label}: ${o.l}`}</option>
        ))}
      </select>
      <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}
